import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'

export class TourManager {
  constructor (router) {
    this.router = router
    this.driverObj = null
    this.steps = []
    this.currentIndex = 0
    this.tourId = null
  }

  async start (tourDef) {
    this.tourId = tourDef.id
    this.steps = tourDef.steps
    this.currentIndex = 0
    this._saveProgress()
    await this._runFromCurrentIndex()
  }

  async resume () {
    const saved = JSON.parse(localStorage.getItem('onboarding_tour_progress') || 'null')
    if (!saved) return false
    return saved
  }

  async _runFromCurrentIndex () {
    const step = this.steps[this.currentIndex]
    if (!step) { this.complete(); return }

    // Navigate if on different route
    if (step.route && this.router.currentRoute.path !== step.route) {
      await this.router.push(step.route)
      await this._waitForElement(step.element, 4000)
    }

    // Collect steps on the same page
    const samePageSteps = this._collectSamePageSteps()

    if (this.driverObj) {
      this.driverObj.destroy()
    }

    const self = this
    this.driverObj = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      progressText: '{{current}} de {{total}}',
      nextBtnText: this.currentIndex + samePageSteps.length >= this.steps.length ? 'Concluir' : 'Próximo',
      prevBtnText: 'Anterior',
      doneBtnText: 'Concluir',
      popoverClass: 'rsvpfy-tour-popover',
      overlayColor: 'rgba(0,0,0,0.5)',
      steps: samePageSteps,
      onNextClick: () => self._handleNext(),
      onPrevClick: () => self._handlePrev(),
      onCloseClick: () => self.skip(),
      onDestroyStarted: () => {
        if (!self.driverObj.hasNextStep()) {
          self._handleNext()
        } else {
          self.driverObj.destroy()
        }
      }
    })

    this.driverObj.drive()
  }

  async _handleNext () {
    if (this.driverObj) {
      this.driverObj.destroy()
    }
    this.currentIndex++
    if (this.currentIndex >= this.steps.length) { this.complete(); return }
    this._saveProgress()
    await this._runFromCurrentIndex()
  }

  async _handlePrev () {
    if (this.currentIndex <= 0) return
    if (this.driverObj) {
      this.driverObj.destroy()
    }
    this.currentIndex--
    this._saveProgress()
    await this._runFromCurrentIndex()
  }

  _collectSamePageSteps () {
    const step = this.steps[this.currentIndex]
    return [{
      element: step.element,
      popover: {
        title: step.title,
        description: step.description,
        side: step.side || 'bottom',
        align: step.align || 'start'
      }
    }]
  }

  complete () {
    const completed = JSON.parse(localStorage.getItem('onboarding_tours_completed') || '[]')
    if (!completed.includes(this.tourId)) completed.push(this.tourId)
    localStorage.setItem('onboarding_tours_completed', JSON.stringify(completed))
    localStorage.removeItem('onboarding_tour_progress')
    if (this.driverObj) {
      this.driverObj.destroy()
      this.driverObj = null
    }
  }

  skip () {
    if (this.driverObj) {
      this.driverObj.destroy()
      this.driverObj = null
    }
    localStorage.removeItem('onboarding_tour_progress')
  }

  _saveProgress () {
    localStorage.setItem('onboarding_tour_progress', JSON.stringify({
      tourId: this.tourId, step: this.currentIndex
    }))
  }

  _waitForElement (selector, timeout = 3000) {
    return new Promise(resolve => {
      const el = document.querySelector(selector)
      if (el) return resolve(el)
      const observer = new MutationObserver(() => {
        const found = document.querySelector(selector)
        if (found) { observer.disconnect(); resolve(found) }
      })
      observer.observe(document.body, { childList: true, subtree: true })
      setTimeout(() => { observer.disconnect(); resolve(null) }, timeout)
    })
  }

  static isCompleted (tourId) {
    const completed = JSON.parse(localStorage.getItem('onboarding_tours_completed') || '[]')
    return completed.includes(tourId)
  }

  static resetTour (tourId) {
    const completed = JSON.parse(localStorage.getItem('onboarding_tours_completed') || '[]')
    const idx = completed.indexOf(tourId)
    if (idx > -1) completed.splice(idx, 1)
    localStorage.setItem('onboarding_tours_completed', JSON.stringify(completed))
    localStorage.removeItem('onboarding_tour_progress')
    localStorage.removeItem('onboarding_banner_dismissed')
  }
}
